"use client"

import { useState, useTransition, useEffect } from "react"
import {
  Modal, Stack, Text, Group, Button, Alert, Divider, Center, Loader,
  TbAlertTriangle, TbCheck,
} from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { calculateSecondaryCommission } from "@forestsync/lib/commission"
import { executeProcurementTrade } from "@/actions/procurement"
import {
  createTradePaymentIntent,
  cancelTradePaymentIntent,
  getTraderBalance,
  createTraderBalancePaymentIntent,
} from "@/actions/trade-payment"

// Load Stripe.js once at module level (Stripe requirement)
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!)

export interface ProcurementTrade {
  projectId: string
  creditType: string
  vintage: number
  price: number
  volume: number
  mandateId?: number
}

interface ProcurementCheckoutModalProps {
  trade: ProcurementTrade | null
  onClose: () => void
  onSuccess: (orderRef: string) => void
}

type Stage = "loading" | "payment" | "success"

/**
 * Maps executeProcurementTrade error codes to procurement i18n strings.
 * Kept as a hook-free helper so both card and balance paths share it.
 */
function useProcurementErrorText() {
  const t = useTranslations("procurement")
  return (res: { error: string; code?: string }) => {
    switch (res.code) {
      case "SUPPLY_GONE": return t("supplyGone")
      case "VALIDATION_FAILED": return t("validationFailed")
      case "AUTH_ERROR": return t("authError")
      case "MANDATE_LIMIT": return t("mandateLimit")
      default: return res.error
    }
  }
}

interface CardFormProps {
  trade: ProcurementTrade
  paymentIntentId: string
  onSuccess: (orderRef: string) => void
}

function CardPaymentForm({ trade, paymentIntentId, onSuccess }: CardFormProps) {
  const t = useTranslations("browse")
  const errorText = useProcurementErrorText()
  const stripe = useStripe()
  const elements = useElements()
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)

  function handleConfirmPay() {
    if (!stripe || !elements) return
    setError(null)
    startTransition(async () => {
      // Authorize the card hold client-side first (same pattern as browse-buy)
      const confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      })
      if (confirmResult.error) {
        setError(confirmResult.error.message ?? t("buyError"))
        return
      }
      const piId = confirmResult.paymentIntent?.id ?? paymentIntentId
      const res = await executeProcurementTrade({ ...trade, paymentIntentId: piId })
      if ("error" in res) {
        // Order did not place — release the authorization hold
        await cancelTradePaymentIntent({ paymentIntentId: piId }).catch(() => null)
        setError(errorText(res))
        return
      }
      onSuccess(res.orderRef)
    })
  }

  return (
    <>
      <PaymentElement />
      {error && (
        <Alert color="red" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}
      <Button fullWidth color="green" autoContrast loading={isPending} onClick={handleConfirmPay}>
        {t("confirmAndPay")}
      </Button>
    </>
  )
}

export function ProcurementCheckoutModal({ trade, onClose, onSuccess }: ProcurementCheckoutModalProps) {
  const t = useTranslations("browse")
  const errorText = useProcurementErrorText()
  const [isPending, startTransition] = useTransition()
  const [stage, setStage] = useState<Stage>("loading")
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [balanceCents, setBalanceCents] = useState<number | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<"card" | "balance">("card")
  const [orderRef, setOrderRef] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const opened = !!trade
  const subtotal = trade ? Math.round(trade.volume * trade.price * 100) / 100 : 0
  // Same conservative fee the server (settlement capture) reconciles against — platformAnnualVolume
  // of 0 yields the highest tier so the pre-auth always covers the captured amount.
  const platformFee = trade
    ? calculateSecondaryCommission({ quantity: trade.volume, pricePerTon: trade.price, platformAnnualVolume: 0 }).commission
    : 0
  const preAuthAmount = Math.round((subtotal + platformFee) * 100) / 100

  // Pre-authorize a card PaymentIntent (and read the trader balance) as soon as the modal opens.
  useEffect(() => {
    if (!opened || preAuthAmount <= 0) return
    let active = true
    setStage("loading")
    setError(null)
    setClientSecret(null)
    setPaymentIntentId(null)
    setBalanceCents(null)
    setPaymentMethod("card")
    startTransition(async () => {
      const [piResult, balanceResult] = await Promise.all([
        createTradePaymentIntent({ amountEur: preAuthAmount }),
        getTraderBalance(),
      ])
      if (!active) return
      if ("error" in piResult) {
        setError(piResult.error ?? t("buyError"))
        return
      }
      setClientSecret(piResult.clientSecret)
      setPaymentIntentId(piResult.paymentIntentId)
      if ("balanceCents" in balanceResult) {
        setBalanceCents(balanceResult.balanceCents)
        if (balanceResult.balanceCents / 100 >= preAuthAmount) setPaymentMethod("balance")
      }
      setStage("payment")
    })
    return () => { active = false }
    // Re-run when the modal opens (opened false→true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  function handleBalanceConfirm() {
    if (!trade) return
    setError(null)
    startTransition(async () => {
      // Release the card hold — this order is funded from balance instead
      if (paymentIntentId) {
        await cancelTradePaymentIntent({ paymentIntentId }).catch(() => null)
      }
      const balancePi = await createTraderBalancePaymentIntent({ amountEur: preAuthAmount })
      if ("error" in balancePi) {
        setError(balancePi.error ?? t("buyError"))
        return
      }
      const res = await executeProcurementTrade({ ...trade, paymentIntentId: balancePi.paymentIntentId })
      if ("error" in res) {
        await cancelTradePaymentIntent({ paymentIntentId: balancePi.paymentIntentId }).catch(() => null)
        setError(errorText(res))
        return
      }
      setOrderRef(res.orderRef)
      setStage("success")
      onSuccess(res.orderRef)
    })
  }

  function handleClose() {
    // Release an un-consumed card hold before closing
    if (stage !== "success" && paymentIntentId) {
      cancelTradePaymentIntent({ paymentIntentId }).catch(() => null)
    }
    setStage("loading")
    setClientSecret(null)
    setPaymentIntentId(null)
    setBalanceCents(null)
    setOrderRef(null)
    setError(null)
    onClose()
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t("confirmAndPay")} centered>
      <Stack gap="md">
        {trade && (
          <>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{t("pricePerCredit")}</Text>
              <Text size="sm" fw={500}>€{trade.price.toFixed(2)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{t("totalPrice")}</Text>
              <Text size="sm" fw={500}>€{subtotal.toFixed(2)}</Text>
            </Group>
            {platformFee > 0 && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">{t("platformFee")}</Text>
                <Text size="sm" fw={500}>€{platformFee.toFixed(2)}</Text>
              </Group>
            )}
            <Divider />
            <Group justify="space-between">
              <Text fw={500}>{t("preAuthorized")}</Text>
              <Text fw={600}>€{preAuthAmount.toFixed(2)}</Text>
            </Group>
            <Text size="xs" c="dimmed">{t("estCostNote")}</Text>
          </>
        )}

        {stage === "loading" && !error && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">{t("preparingPayment")}</Text>
            </Stack>
          </Center>
        )}

        {stage === "payment" && (
          <>
            {/* Payment method selector — only when the trader has a usable balance */}
            {balanceCents !== null && balanceCents > 0 && (
              <>
                <Divider label={t("paymentMethodLabel")} labelPosition="center" />
                <Group grow>
                  <Button
                    variant={paymentMethod === "balance" ? "filled" : "light"}
                    color="teal"
                    autoContrast
                    disabled={balanceCents / 100 < preAuthAmount}
                    onClick={() => setPaymentMethod("balance")}
                  >
                    {t("paymentBalance")} · €{(balanceCents / 100).toFixed(2)}
                  </Button>
                  <Button
                    variant={paymentMethod === "card" ? "filled" : "light"}
                    color="blue"
                    autoContrast
                    onClick={() => setPaymentMethod("card")}
                  >
                    {t("paymentCard")}
                  </Button>
                </Group>
              </>
            )}

            {paymentMethod === "card" && clientSecret && paymentIntentId ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CardPaymentForm
                  trade={trade!}
                  paymentIntentId={paymentIntentId}
                  onSuccess={(ref) => {
                    setOrderRef(ref)
                    setStage("success")
                    onSuccess(ref)
                  }}
                />
              </Elements>
            ) : paymentMethod === "balance" ? (
              <>
                {error && (
                  <Alert color="red" onClose={() => setError(null)} withCloseButton>{error}</Alert>
                )}
                <Button
                  fullWidth
                  color="green"
                  autoContrast
                  loading={isPending}
                  disabled={balanceCents !== null && balanceCents / 100 < preAuthAmount}
                  onClick={handleBalanceConfirm}
                >
                  {t("confirmAndPay")}
                </Button>
              </>
            ) : null}
          </>
        )}

        {stage === "success" && (
          <Alert color="green" icon={<TbCheck size={16} />}>
            {orderRef ? `${t("orderRef")}: ${orderRef}` : t("confirmAndPay")}
          </Alert>
        )}

        {error && stage !== "payment" && (
          <Alert color="red" icon={<TbAlertTriangle size={16} />} onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}
      </Stack>
    </Modal>
  )
}
